import logger from '../../lib/log';
import { PaymentError } from '../../lib/error';
import {
  getAppAuthorizationUrl, getAppAuthorizationFailureUrl, getBraintreeInstance, selectPaymentMethod, updatePaymentInfo,
} from '../../lib/wallets';

// https://developers.braintreepayments.com/guides/google-pay/client-side/javascript/v3

export const PAYMENT_METHOD_ID = 'androidpay';
export const PAYMENT_METHOD_NAME = 'G-Pay';
export const ICON_FILENAME = 'payment-google-pay-icon.svg';
export const AUTO_COMPLETE_PURCHASE = true;

const log = logger('GooglePay');

let authorized = false;
let braintreePaymentInstance;
let fandangoToken;
let paymentsClient;
let postalCode;
let isAppWebView = false;
let rejectNativeAuthorization;
let resolveNativeAuthorization;

/**
 * getBraintreeNonce
 *
 * Tokenizes the payment method and returns the Braintree nonce needed to complete the purchase.
 *
 * @async
 * @param {decimal} purchaseTotal - The total price for the transaction
 *
 * @returns {String} Braintree nonce
 * @throws  {PaymentError}
 */
const getBraintreeNonce = async (purchaseTotal) => {
  if (!braintreePaymentInstance) throw new PaymentError();

  const paymentDataRequest = braintreePaymentInstance.createPaymentDataRequest({
    transactionInfo: {
      currencyCode: 'USD',
      totalPriceStatus: 'FINAL',
      totalPrice: _.toString(purchaseTotal), // This NEEDS to be a string or things will fail
    },
  });

  log('paymentDataRequest', paymentDataRequest);

  // Best practice to collect and pass along the postal code with all transactions (and keep transaction fees lower)
  const cardPaymentMethod = paymentDataRequest.allowedPaymentMethods[0];
  log('cardPaymentMethod', cardPaymentMethod);
  cardPaymentMethod.parameters.billingAddressRequired = true;
  cardPaymentMethod.parameters.billingAddressParameters = {
    format: 'MIN', // 'FULL'
    phoneNumberRequired: false,
  };

  return paymentsClient.loadPaymentData(paymentDataRequest)
    .then((paymentData) => {
      postalCode = paymentData?.paymentMethodData?.info?.billingAddress?.postalCode;
      return braintreePaymentInstance.parseResponse(paymentData);
    })
    .then((results) => {
      log('google pay results', results);
      return results;
    })
    .then((result) => result.nonce)
    .catch((err) => {
      if (err.statusCode === 'CANCELED') return null;
      throw new PaymentError(err);
    });
};

const onAuthorizationFailure = () => {
  if (isAppWebView) {
    window.open(getAppAuthorizationFailureUrl(PAYMENT_METHOD_ID), '_self');
  }
};

/**
 * init
 *
 * Initializes GooglePay as a payment method and uses the google api to ensure
 * that it can be used as a payment method and prepares the Braintree
 * instance for use downstream during authorization.
 *
 * @async
 * @param {Object} options
 * @param {String} options.googlePayEnvironment
 * @param {String} options.googlePayMerchantId
 * @param {Boolean} options.isAppWebView
 *
 * @returns {Boolean} Flag to indicate that GooglePay has been initialized
 *   successfully and can be used as a payment method.
 */
export const init = async (options) => {
  isAppWebView = options.isAppWebView;

  try {
    braintreePaymentInstance = await getBraintreeInstance({
      paymentMethodId: PAYMENT_METHOD_ID,
      config: {
        googlePayVersion: 2,
        googleMerchantId: options.googlePayMerchantId,
      },
    });

    paymentsClient = new window.google.payments.api.PaymentsClient({
      environment: options.googlePayEnvironment,
    });

    const response = await paymentsClient.isReadyToPay({
      // see https://developers.google.com/pay/api/web/reference/object#IsReadyToPayRequest
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: braintreePaymentInstance.createPaymentDataRequest().allowedPaymentMethods,
    });

    log('isReadyToPay?', response);
    return response && response.result;
  } catch (err) {
    log('DISABLED - error', err);
    return false;
  }
};

/**
 * select
 *
 * Saves GooglePay as the selected payment method
 *
 * @async
 * @throws {PaymentError} Any errors thrown by `selectPaymentMethod` are simply propagated here
 */
export const select = async () => {
  const response = await selectPaymentMethod({
    paymentMethodId: PAYMENT_METHOD_ID,
  });
  fandangoToken = response.fandangoToken;
};

/**
 * authorize
 *
 * Authorizes GooglePay as the payment method for the transaction and saves the Braintree nonce which is
 * used to complete the purchase.
 *
 * NOTE: If the user is within a native app the authorization will be handled *externally* so this simply notifies
 * the app to start that process and when it is complete (and it has the nonce) it will then call `completeNativeAuthorization`
 * to complete the process.
 *
 * @async
 * @throws {PaymentError}
 */
export const authorize = async ({ purchaseTotal }) => {
  if (isAppWebView) {
    return new Promise((resolve, reject) => {
      // This promise will be Fulfilled by `completeNativeAuthorization` which is triggered by the app
      resolveNativeAuthorization = resolve;
      rejectNativeAuthorization = reject;
      const authorizePaymentUrl = getAppAuthorizationUrl(PAYMENT_METHOD_ID, fandangoToken, purchaseTotal);
      log('initializing native authorization', authorizePaymentUrl);
      window.open(authorizePaymentUrl, '_self');
    });
  }

  try {
    const braintreeNonce = await getBraintreeNonce(purchaseTotal);
    if (!braintreeNonce) return; // The user canceled - simply exit with completing the authorization process

    await updatePaymentInfo({
      braintreeNonce,
      postalCode,
      walletId: PAYMENT_METHOD_ID,
    });
    authorized = true;
  } catch (err) {
    onAuthorizationFailure();
    authorized = false;
    throw new PaymentError(err);
  }
};

/**
 * completeNativeAuthorization
 *
 * Completes the authorization process for the native payment method
 * This will resolve the promise returned by `authorize`
 *
 * NOTE: This function is exposed directly on the winow so that the app can call it directly
 *
 * @async
 * @param {String} nonce - Braintree nonce
 * @param {Object} params - Additional data from Android app
 * @param {String} params.billingZipCode
 */
export const completeNativeAuthorization = async (nonce, params = {}) => {
  log('complete native authorization');
  if (!resolveNativeAuthorization || !rejectNativeAuthorization) return;

  if (!nonce) {
    onAuthorizationFailure();
    authorized = false;
    rejectNativeAuthorization(new PaymentError('unknown nonce'));
    return;
  }

  try {
    await updatePaymentInfo({
      braintreeNonce: nonce,
      postalCode: params.billingZipCode || postalCode,
      walletId: PAYMENT_METHOD_ID,
    });
    authorized = true;
    resolveNativeAuthorization();
  } catch (err) {
    onAuthorizationFailure();
    authorized = false;
    rejectNativeAuthorization(new PaymentError(err));
  }
};

export const isAuthorized = () => authorized;

export default PAYMENT_METHOD_ID;
